import React, { Component } from 'react';
import API from '../../utils/API';
import { Modal } from 'reactstrap';

class VenueSearchModal extends Component {
	constructor(props) {
		super(props);
		this.state = {
			// Wizard step (non-proposal mode only)
			step: 1, // 1 = search/select, 2 = event details

			// Search filters
			searchName: '',
			searchLocation: '',
			searchCapacity: '',
			searchEventType: '',
			searchStyle: '',

			// Results and selection
			searchResults: [],
			selectedVenues: {},
			searching: false,
			searched: false,

			// Detail panel
			detailVenue: null,
			detailPanelOpen: false,

			// Step 2: Event details
			eventName: '',
			eventType: 'Corporate',
			arrivalDate: '',
			departureDate: '',
			estimatedAttendees: '',
			copyFromVenueId: '',
			existingVenues: [],

			// Adding state
			adding: false,
			addedCount: 0
		};
	}

	async componentDidMount() {
		try {
			const response = await API().get(`Organizations/${this.props.orgId}/sourced-venues`);
			if (response.data) {
				const venuesWithEvents = response.data.filter(v => v.eventDetails && v.eventDetails.eventName);
				this.setState({ existingVenues: venuesWithEvents });
			}
		} catch (error) {
			console.error('Error loading venues:', error);
		}
	}

	goToStep2 = () => { this.setState({ step: 2 }); };
	goBackToStep1 = () => { this.setState({ step: 1 }); };

	loadRFPFromVenue = async (venueId) => {
		if (!venueId) {
			this.setState({ eventName: '', eventType: 'Corporate', arrivalDate: '', departureDate: '', estimatedAttendees: '' });
			return;
		}
		try {
			const response = await API().get(`Organizations/${this.props.orgId}/sourced-venues/${venueId}`);
			if (response.data && response.data.eventDetails) {
				const ed = response.data.eventDetails;
				this.setState({
					eventName: ed.eventName || '',
					eventType: ed.eventType || 'Corporate',
					arrivalDate: ed.arrivalDate ? new Date(ed.arrivalDate).toISOString().split('T')[0] : '',
					departureDate: ed.departureDate ? new Date(ed.departureDate).toISOString().split('T')[0] : '',
					estimatedAttendees: ed.estimatedAttendees || ''
				});
			}
		} catch (error) {
			console.error('Error loading RFP details:', error);
		}
	};

	handleSearch = async () => {
		this.setState({ searching: true, searched: false });
		const { searchName, searchLocation, searchCapacity, searchEventType, searchStyle } = this.state;

		const searchParams = {
			preferences: {
				name: searchName,
				eventType: searchEventType,
				guestCount: parseInt(searchCapacity),
				style: searchStyle,
				notes: `${searchName} - ${searchLocation}`
			},
			excludeIds: [],
			sessionId: Date.now().toString()
		};

		try {
			const response = await API().post(`Organizations/${this.props.orgId}/sourced-venues/search`, searchParams);
			if (response.data.success && response.data.venues) {
				this.setState({ searchResults: response.data.venues, searching: false, searched: true });
			} else {
				this.setState({ searchResults: [], searching: false, searched: true });
			}
		} catch (error) {
			console.error('Error searching venues:', error);
			this.setState({ searchResults: [], searching: false, searched: true });
		}
	};

	toggleVenueSelection = (venueId) => {
		const { selectedVenues } = this.state;
		if (selectedVenues[venueId]) {
			const updated = { ...selectedVenues };
			delete updated[venueId];
			this.setState({ selectedVenues: updated });
		} else {
			this.setState({ selectedVenues: { ...selectedVenues, [venueId]: true } });
		}
	};

	openDetailPanel = (venue) => {
		this.setState({ detailVenue: venue, detailPanelOpen: true });
	};

	closeDetailPanel = () => {
		this.setState({ detailVenue: null, detailPanelOpen: false });
	};

	addSelectedVenues = async () => {
		const { selectedVenues, searchResults, eventName, eventType, arrivalDate, departureDate, estimatedAttendees } = this.state;
		const venueIds = Object.keys(selectedVenues);
		const isProposalMode = this.props.mode === 'proposalRequest';
		const proposalRequestId = this.props.proposalRequestId;

		if (venueIds.length === 0) return;

		this.setState({ adding: true, addedCount: 0 });
		const sourceDate = new Date();

		for (let i = 0; i < venueIds.length; i++) {
			const venueId = venueIds[i];
			const venue = searchResults.find(v => v.id === venueId);

			if (venue) {
				try {
					// Build a capacity object from whatever the search result provides
					const capacityMax = venue.capacity
						? (typeof venue.capacity === 'object' ? venue.capacity.max : parseInt(venue.capacity))
						: null;

					const venueData = {
						name: venue.name,
						description: venue.description || venue.enhancedDescription || '',
						venueType: 'Physical',
						location: venue.location || '',
						website: venue.contact?.website || venue.website || '',
						phoneNumber: venue.contact?.phone || venue.phoneNumber || '',
						contactEmail: venue.contactEmail || venue.contact?.email || '',
						timezone: '',
						// Store image under both keys so every consumer finds it
						primaryImage: venue.img || '',
						imageUrl: venue.img || '',
						images: venue.additionalImages || [],
						amenities: venue.amenities || [],
						// Capacity as structured object so detail views can read .max
						...(capacityMax ? { capacity: { max: capacityMax } } : {}),
						// Guest room count when provided by the search result
						...(venue.totalRooms ? { totalRooms: venue.totalRooms } : {}),
						...(venue.rooms    ? { totalRooms: venue.rooms }      : {}),
						// Rating / review data
						...(venue.review > 0
							? { rating: venue.review, totalReviews: venue.totalReviews || 0 }
							: {}),
						// Preserve the external id so we can re-fetch details later
						...(venue.id ? { externalId: venue.id } : {}),
						...(venue.placeId ? { placeId: venue.placeId } : {}),
						source: 'search',
						sourceDate
					};

					if (eventName) {
						venueData.eventDetails = {
							eventName,
							eventType,
							arrivalDate: arrivalDate || null,
							departureDate: departureDate || null,
							estimatedAttendees: parseInt(estimatedAttendees) || 0
						};
					}

					const createRes = await API().post(`Organizations/${this.props.orgId}/sourced-venues`, venueData);
					const createdVenue = createRes.data;

					if (isProposalMode && createdVenue && createdVenue._id && proposalRequestId) {
						await API().post(
							`Organizations/${this.props.orgId}/proposal-requests/${proposalRequestId}/venues`,
							{ venueId: createdVenue._id }
						);
					}

					this.setState({ addedCount: this.state.addedCount + 1 });
				} catch (error) {
					console.error('Error adding venue:', error);
				}
			}
		}

		setTimeout(() => {
			this.props.onVenuesAdded && this.props.onVenuesAdded();
			this.props.toggle();
			this.setState({
				step: 1,
				searchName: '', searchLocation: '', searchCapacity: '', searchEventType: '', searchStyle: '',
				searchResults: [], selectedVenues: {}, searched: false,
				eventName: '', eventType: 'Corporate', arrivalDate: '', departureDate: '',
				estimatedAttendees: '', copyFromVenueId: '', adding: false, addedCount: 0
			});
		}, 500);
	};

	clearSearch = () => {
		this.setState({
			searchName: '', searchLocation: '', searchCapacity: '', searchEventType: '', searchStyle: '',
			searchResults: [], selectedVenues: {}, searched: false
		});
	};

	renderSearchStep() {
		const { searchName, searchLocation, searchResults, selectedVenues, searching, searched, detailVenue, detailPanelOpen } = this.state;

		return (
			<div className="vsm-search-layout">
				{/* Search bar */}
				<div className="vsm-search-bar">
					<div className="vsm-search-inputs">
						<div className="vsm-search-field">
							<i className="las la-hotel" />
							<input
								className="vsm-text-input"
								placeholder="Hotel or venue name..."
								value={searchName}
								onChange={(e) => this.setState({ searchName: e.target.value })}
								onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); this.handleSearch(); } }}
							/>
						</div>
						<div className="vsm-search-field">
							<i className="las la-map-marker-alt" />
							<input
								className="vsm-text-input"
								placeholder="City, State..."
								value={searchLocation}
								onChange={(e) => this.setState({ searchLocation: e.target.value })}
								onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); this.handleSearch(); } }}
							/>
						</div>
						<button
							type="button"
							className="pr-btn-primary"
							onClick={this.handleSearch}
							disabled={searching}
						>
							<i className="las la-search" />
							{searching ? 'Searching...' : 'Search'}
						</button>
						{searched && (
							<button type="button" className="pr-btn-ghost" onClick={this.clearSearch}>
								Clear
							</button>
						)}
					</div>
				</div>

				{/* Content area: optional detail panel + results */}
				<div className="vsm-content">

					{/* Venue detail side panel */}
					{detailPanelOpen && detailVenue && (
						<div className="vsm-detail-panel">
							<div className="vsm-detail-header">
								<button type="button" className="pr-icon-btn" onClick={this.closeDetailPanel} style={{ marginBottom: 8 }}>
									<i className="las la-times" />
								</button>
								<h2 className="vsm-detail-name">{detailVenue.name}</h2>
								{detailVenue.location && (
									<p className="vsm-detail-location">
										<i className="las la-map-marker-alt" /> {detailVenue.location}
									</p>
								)}
								<button
									type="button"
									className={selectedVenues[detailVenue.id] ? 'pr-btn-ghost' : 'pr-btn-primary'}
									onClick={() => this.toggleVenueSelection(detailVenue.id)}
									style={{ marginTop: 8 }}
								>
									{selectedVenues[detailVenue.id] ? (
										<><i className="las la-check" /> Selected</>
									) : (
										<><i className="las la-plus" /> Select</>
									)}
								</button>
							</div>
							{detailVenue.img && (
								<img src={detailVenue.img} alt={detailVenue.name} className="vsm-detail-img" />
							)}
							<div className="vsm-detail-body">
								{(detailVenue.description || detailVenue.enhancedDescription) && (
									<p className="vsm-detail-desc">
										{detailVenue.description || detailVenue.enhancedDescription}
									</p>
								)}
								{detailVenue.capacity && (
									<p style={{ fontSize: 13, color: '#555', marginBottom: 12 }}>
										<strong>Capacity:</strong> Up to {detailVenue.capacity.toLocaleString()} guests
									</p>
								)}
								{detailVenue.review > 0 && (
									<p style={{ fontSize: 13, color: '#555', marginBottom: 12 }}>
										<i className="las la-star" style={{ marginRight: 4 }} />
										{detailVenue.review.toFixed(1)}
										{detailVenue.totalReviews > 0 && ` (${detailVenue.totalReviews} reviews)`}
									</p>
								)}
								{detailVenue.amenities?.length > 0 && (
									<div style={{ marginTop: 8 }}>
										{detailVenue.amenities.map((a, i) => (
											<span key={i} className="vsm-amenity-tag">{a}</span>
										))}
									</div>
								)}
								{detailVenue.contact?.phone && (
									<p style={{ fontSize: 13, color: '#555', marginTop: 16, marginBottom: 4 }}>
										<i className="las la-phone" style={{ marginRight: 6 }} />
										{detailVenue.contact.phone}
									</p>
								)}
								{detailVenue.contact?.website && (
									<a
										href={detailVenue.contact.website}
										target="_blank"
										rel="noopener noreferrer"
										style={{ fontSize: 13, color: '#1a1a1a', display: 'flex', alignItems: 'center', gap: 6, marginTop: 4 }}
									>
										<i className="las la-globe" /> Website
									</a>
								)}
							</div>
						</div>
					)}

					{/* Search results */}
					<div className="vsm-results">
						{searching && (
							<div className="vsm-empty">
								<i className="las la-spinner vsm-spin" style={{ fontSize: 36 }} />
								<p>Searching for venues…</p>
							</div>
						)}

						{!searching && !searched && (
							<div className="vsm-empty">
								<i className="las la-search-location" />
								<p>Search by name or location to find venues.</p>
							</div>
						)}

						{!searching && searched && searchResults.length === 0 && (
							<div className="vsm-empty">
								<i className="las la-hotel" />
								<h3>No venues found</h3>
								<p>Try adjusting your search terms.</p>
							</div>
						)}

						{!searching && searchResults.length > 0 && (
							<div className="vsm-venue-grid">
								{searchResults.map((venue) => {
									const isSelected = !!selectedVenues[venue.id];
									return (
										<div
											key={venue.id}
											className={`vsm-venue-card ${isSelected ? 'selected' : ''}`}
										>
											<div
												className={`vsm-venue-check ${isSelected ? 'checked' : ''}`}
												onClick={(e) => { e.stopPropagation(); this.toggleVenueSelection(venue.id); }}
											>
												{isSelected && <i className="las la-check" />}
											</div>
											{venue.img && (
												<img
													src={venue.img}
													alt={venue.name}
													className="vsm-venue-img"
													onClick={() => this.openDetailPanel(venue)}
												/>
											)}
											<div className="vsm-venue-info">
												<h3
													className="vsm-venue-name"
													onClick={() => this.openDetailPanel(venue)}
												>
													{venue.name}
												</h3>
												{venue.location && (
													<p className="vsm-venue-location">
														<i className="las la-map-marker-alt" /> {venue.location}
													</p>
												)}
												{venue.review > 0 && (
													<p className="vsm-venue-rating">
														<i className="las la-star" />
														{venue.review.toFixed(1)}
														{venue.totalReviews > 0 && ` (${venue.totalReviews})`}
													</p>
												)}
											</div>
										</div>
									);
								})}
							</div>
						)}
					</div>
				</div>
			</div>
		);
	}

	renderDetailsStep() {
		const { eventName, eventType, arrivalDate, departureDate, estimatedAttendees, copyFromVenueId, existingVenues } = this.state;
		const selectedCount = Object.keys(this.state.selectedVenues).length;

		const rfpEventTypeOptions = [
			{ value: 'Corporate', label: 'Corporate' },
			{ value: 'Incentive', label: 'Incentive' },
			{ value: 'Association', label: 'Association' },
			{ value: 'Government', label: 'Government' },
			{ value: 'Sports', label: 'Sports' },
			{ value: 'Tour Groups', label: 'Tour Groups' },
			{ value: 'Wedding', label: 'Wedding' },
			{ value: 'Other', label: 'Other' }
		];

		const copyFromOptions = [
			{ value: '', label: 'Start fresh...' },
			...existingVenues.map(v => ({
				value: v._id,
				label: v.eventDetails?.eventName || v.name
			}))
		];

		return (
			<div className="vsm-step2">
				<div className="vsm-step2-content">
					<h1 className="vsm-step2-title">Event Details</h1>
					<p className="vsm-step2-sub">
						Attach event details to all {selectedCount} selected venue{selectedCount !== 1 ? 's' : ''} (optional).
					</p>

					{existingVenues.length > 0 && (
						<div className="pr-field-group">
							<label className="pr-field-label">Copy details from existing RFP</label>
							<div className="pr-select-wrap">
								<select
									className="pr-select"
									value={copyFromVenueId}
									onChange={(e) => {
										const val = e.target.value;
										this.setState({ copyFromVenueId: val });
										this.loadRFPFromVenue(val);
									}}
								>
									{copyFromOptions.map(opt => (
										<option key={opt.value} value={opt.value}>{opt.label}</option>
									))}
								</select>
								<i className="las la-angle-down pr-select-arrow" />
							</div>
						</div>
					)}

					<div className="pr-field-group">
						<label className="pr-field-label">Event Name</label>
						<input
							className="pr-text-input"
							value={eventName}
							onChange={(e) => this.setState({ eventName: e.target.value })}
							placeholder="e.g., Annual Sales Conference 2026"
						/>
					</div>

					<div className="pr-field-group">
						<label className="pr-field-label">Event Type</label>
						<div className="pr-select-wrap">
							<select
								className="pr-select"
								value={eventType}
								onChange={(e) => this.setState({ eventType: e.target.value })}
							>
								{rfpEventTypeOptions.map(opt => (
									<option key={opt.value} value={opt.value}>{opt.label}</option>
								))}
							</select>
							<i className="las la-angle-down pr-select-arrow" />
						</div>
					</div>

					<div className="pr-row-2">
						<div className="pr-field-group">
							<label className="pr-field-label">Arrival Date</label>
							<input
								className="pr-text-input"
								type="date"
								value={arrivalDate}
								onChange={(e) => this.setState({ arrivalDate: e.target.value })}
							/>
						</div>
						<div className="pr-field-group">
							<label className="pr-field-label">Departure Date</label>
							<input
								className="pr-text-input"
								type="date"
								value={departureDate}
								min={arrivalDate}
								onChange={(e) => this.setState({ departureDate: e.target.value })}
							/>
						</div>
					</div>

					<div className="pr-field-group">
						<label className="pr-field-label">Estimated Attendees</label>
						<input
							className="pr-text-input large"
							type="number"
							value={estimatedAttendees}
							onChange={(e) => this.setState({ estimatedAttendees: e.target.value })}
							placeholder="0"
						/>
					</div>
				</div>
			</div>
		);
	}

	render() {
		const { isOpen, toggle } = this.props;
		const { step, selectedVenues, adding, addedCount } = this.state;
		const selectedCount = Object.keys(selectedVenues).length;
		const isProposalMode = this.props.mode === 'proposalRequest';

		const actionLabel = (() => {
			if (adding) return `Adding ${addedCount}/${selectedCount}…`;
			if (isProposalMode || step === 2) {
				return selectedCount > 0
					? `Add ${selectedCount} Venue${selectedCount !== 1 ? 's' : ''}`
					: 'Add Venues';
			}
			return 'Next';
		})();

		return (
			<Modal
				isOpen={isOpen}
				toggle={toggle}
				className="venueSearchModal"
				modalClassName="venueSearchModalWrapper"
				fullscreen
				fade={false}
			>
				<div className="vsm-root">
					{/* Header */}
					<div className="vsm-header">
						<div className="vsm-header-left">
							{step === 2 && !isProposalMode ? (
								<button type="button" className="vsm-header-cancel" onClick={this.goBackToStep1}>
									<i className="las la-angle-left" />
									Back
								</button>
							) : (
								<button type="button" className="vsm-header-cancel" onClick={toggle}>
									<i className="las la-arrow-left" />
									Cancel
								</button>
							)}
						</div>

						<div className="vsm-header-title">
							<i className="las la-hotel" />
							{isProposalMode ? 'Add Venues to Request' : step === 1 ? 'Search Venues' : 'Event Details'}
						</div>

						<div className="vsm-header-right">
							{selectedCount > 0 && !adding && (
								<span className="vsm-selected-badge">{selectedCount} selected</span>
							)}
							<button
								type="button"
								className="vsm-header-submit-btn"
								onClick={isProposalMode || step === 2 ? this.addSelectedVenues : this.goToStep2}
								disabled={selectedCount === 0 || adding}
							>
								{actionLabel}
								{!adding && !isProposalMode && step === 1 && (
									<i className="las la-angle-right" />
								)}
							</button>
						</div>
					</div>

					{/* Body */}
					<div className="vsm-body">
						{step === 1 && this.renderSearchStep()}
						{step === 2 && this.renderDetailsStep()}
					</div>
				</div>
			</Modal>
		);
	}
}

export default VenueSearchModal;
